/**
 * Resolving which navigation entry describes the current page. Registries supply plain sections,
 * items, and optional children; this module has no seller or shopper context.
 */

const stripTrailingSlash = path => (
  path.length > 1 ? path.replace(/\/+$/, '') : path
);

/** Split an href into `{ path, search, hash }`, tolerating relative and absolute forms. */
export function parseHref(href) {
  if (typeof href !== 'string' || !href.trim()) return null;

  const withoutOrigin = href.trim().replace(/^[a-z][a-z0-9+.-]*:\/\/[^/]+/i, '');
  const [beforeHash, ...hashParts] = (withoutOrigin || '/').split('#');
  const hash = hashParts.length ? `#${hashParts.join('#')}` : '';
  const [path, ...searchParts] = beforeHash.split('?');
  const search = searchParts.length ? `?${searchParts.join('?')}` : '';
  return { path: stripTrailingSlash(path || '/'), search, hash };
}

/** Accepts an href string or a `window.location`-shaped object. */
export function toLocationParts(source) {
  if (!source) return null;
  if (typeof source === 'string') return parseHref(source);

  const { pathname, search = '', hash = '' } = source;
  if (typeof pathname !== 'string' || !pathname) return null;
  return { path: stripTrailingSlash(pathname), search, hash };
}

/** Score how well `href` describes `current`; 0 means no match. */
export function scoreHrefMatch(href, current) {
  const candidate = parseHref(href);
  if (!candidate || !current) return 0;

  let score;
  if (candidate.path === current.path) {
    score = 1000 + candidate.path.length;
  } else if (current.path.startsWith(`${candidate.path}/`)) {
    score = 500 + candidate.path.length;
  } else {
    return 0;
  }

  if (candidate.search) {
    if (candidate.search !== current.search) return 0;
    score += 40;
  }
  if (candidate.hash) {
    if (candidate.hash !== current.hash) return 0;
    score += 20;
  }
  return score;
}

const EMPTY_ACTIVE = Object.freeze({ sectionId: null, itemId: null, childId: null });

function findById(sections, activeId) {
  for (const section of sections) {
    for (const item of section.items) {
      if (item.id === activeId) {
        return { sectionId: section.id, itemId: item.id, childId: null };
      }
      for (const child of item.children || []) {
        if (child.id === activeId || `${item.id}-${child.id}` === activeId) {
          return { sectionId: section.id, itemId: item.id, childId: child.id };
        }
      }
    }
  }
  return null;
}

/** Resolve a navigation trail from an explicit server hint or a scored URL match. */
export function resolveActiveNav(sections, options = {}) {
  const { activeId = null, activeHref = null, location = null } = options;

  if (activeId) {
    const byId = findById(sections, activeId);
    if (byId) return byId;
  }

  const current = toLocationParts(activeHref || location);
  if (!current) return { ...EMPTY_ACTIVE };

  let best = { score: 0, ...EMPTY_ACTIVE };
  sections.forEach(section => {
    section.items.forEach(item => {
      const itemScore = scoreHrefMatch(item.href, current);
      if (itemScore > best.score) {
        best = { score: itemScore, sectionId: section.id, itemId: item.id, childId: null };
      }
      (item.children || []).forEach(child => {
        const childScore = scoreHrefMatch(child.href, current);
        if (childScore > best.score) {
          best = {
            score: childScore,
            sectionId: section.id,
            itemId: item.id,
            childId: child.id,
          };
        }
      });
    });
  });

  return {
    sectionId: best.sectionId,
    itemId: best.itemId,
    childId: best.childId,
  };
}
